import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import ProjectMap from "../components/ProjectMap";
import AAYDADashboard from "../components/AAYDADashboard";
import {
  FolderKanban,
  ClipboardCheck,
  Layers,
  Users,
  Hammer,
  Wallet,
  ShieldCheck,
  Activity,
  Inbox,
  TrendingUp,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Map as MapIcon,
  BarChart3,
} from "lucide-react";

const StatCard = ({
  icon: Icon,
  label,
  value,
  gradient = "from-[#3B82F6] to-[#1D4ED8]",
  trend,
  testid,
}) => (
  <div
    className="card-soft p-5 hover:-translate-y-1 transition-all relative overflow-hidden"
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-4">
      <div
        className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg shadow-slate-900/10`}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      {trend != null && (
        <div
          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full ${
            trend >= 0
              ? "bg-emerald-50 text-emerald-600"
              : "bg-rose-50 text-rose-600"
          }`}
        >
          <TrendingUp className={`w-3 h-3 ${trend < 0 ? "rotate-180" : ""}`} />
          {trend >= 0 ? "+" : ""}
          {trend}%
        </div>
      )}
    </div>
    <div className="font-heading text-4xl font-black tracking-tight text-slate-900">
      {value}
    </div>
    <div className="label-overline mt-1.5">{label}</div>
  </div>
);

function SuperAdminDash({ summary, t }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        icon={Users}
        label={t("users")}
        value={summary.users_total}
        gradient="from-[#3B82F6] to-[#1D4ED8]"
        trend={8}
        testid="sa-users"
      />
      <StatCard
        icon={FolderKanban}
        label={t("projects")}
        value={summary.projects_total}
        gradient="from-[#F97316] to-[#C2410C]"
        trend={2}
        testid="sa-projects"
      />
      <StatCard
        icon={ShieldCheck}
        label={t("role_label.consulting_team_lead")}
        value={summary.users_by_role?.consulting_team_lead || 0}
        gradient="from-[#10B981] to-[#047857]"
      />
      <StatCard
        icon={Activity}
        label={t("contractors")}
        value={summary.users_by_role?.contractor || 0}
        gradient="from-[#A855F7] to-[#7E22CE]"
        trend={12}
      />
    </div>
  );
}

function AAYDAChairmanDash({ summary, t }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderKanban} label={t("projects")} value={summary.projects} gradient="from-[#3B82F6] to-[#1D4ED8]" testid="ch-projects" />
        <StatCard icon={ClipboardCheck} label={t("active_inspections")} value={summary.active_inspections} gradient="from-[#F97316] to-[#C2410C]" />
        <StatCard icon={CheckCircle2} label={t("approved_label")} value={summary.approved} gradient="from-[#10B981] to-[#047857]" />
        <StatCard icon={XCircle} label={t("rejected_label")} value={summary.rejected} gradient="from-[#EF4444] to-[#B91C1C]" />
      </div>
      <div className="bg-gradient-to-br from-[#0A1128] to-[#1A1F4A] text-white rounded-2xl p-6 mt-4 shadow-xl">
        <div className="label-overline text-blue-200">{t("total_estimate")}</div>
        <div className="font-mono text-5xl font-black mt-2 tracking-tighter">
          {summary.total_estimated_cost?.toLocaleString()} ₼
        </div>
        <div className="text-sm text-blue-200 mt-2">
          {t("total_estimate_caption")}
        </div>
      </div>
    </>
  );
}

function AAYDAFinanceDash({ summary, t }) {
  const stages = summary.cost_by_stage_type || {};
  const total = summary.total_estimated_cost || 0;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Wallet}
          label={t("total_estimate_short")}
          value={`${(total / 1000).toFixed(1)}K ₼`}
          gradient="from-[#F97316] to-[#C2410C]"
          testid="fin-total"
        />
        <StatCard
          icon={AlertTriangle}
          label={t("pending_approval")}
          value={summary.pending_approval}
          gradient="from-[#F59E0B] to-[#D97706]"
        />
        <StatCard
          icon={TrendingUp}
          label={t("stage_types")}
          value={Object.keys(stages).length}
          gradient="from-[#3B82F6] to-[#1D4ED8]"
        />
      </div>
      <section className="card-soft p-6 mt-4">
        <h3 className="font-heading font-bold text-lg mb-4">{t("stage_types_distribution")}</h3>
        <div className="space-y-3">
          {Object.entries(stages).map(([type, cost]) => (
            <div key={type}>
              <div className="flex justify-between mb-1">
                <span className="text-sm">{t(type)}</span>
                <span className="mono font-bold">{cost.toLocaleString()} ₼</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-full"
                  style={{ width: `${total ? (cost / total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function AAYDAConstructionDash({ summary, t }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard icon={FolderKanban} label={t("projects")} value={summary.projects} gradient="from-[#3B82F6] to-[#1D4ED8]" />
      <StatCard
        icon={Activity}
        label={t("active_segments")}
        value={summary.active_segments}
        gradient="from-[#10B981] to-[#047857]"
      />
      <StatCard
        icon={ClipboardCheck}
        label={t("pending_inspection")}
        value={summary.stages_pending_inspection}
        gradient="from-[#F59E0B] to-[#D97706]"
      />
    </div>
  );
}

function AAYDATechSupervisionDash({ summary, t }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard icon={FolderKanban} label={t("projects")} value={summary.projects} />
      <StatCard
        icon={ClipboardCheck}
        label={t("active_inspections")}
        value={summary.active_inspections}
        accent="#FF5722"
      />
      <StatCard
        icon={AlertTriangle}
        label={t("correction_requests")}
        value={summary.sub_requests}
        gradient="from-[#EF4444] to-[#B91C1C]"
      />
    </div>
  );
}

function ConsultingDash({ summary, t }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Inbox}
          label={t("waiting_for_action")}
          value={summary.my_queue_count}
          gradient="from-[#F97316] to-[#C2410C]"
          testid="con-queue"
        />
        <StatCard
          icon={ClipboardCheck}
          label={t("active_inspections")}
          value={summary.all_active_inspections}
          gradient="from-[#3B82F6] to-[#1D4ED8]"
        />
        <StatCard
          icon={FolderKanban}
          label={t("my_projects")}
          value={summary.my_projects || 0}
          gradient="from-[#10B981] to-[#047857]"
        />
      </div>
      <Link
        to="/queue"
        className="block bg-gradient-to-r from-[#0A1128] to-[#1A1F4A] text-white rounded-2xl p-5 mt-4 hover:shadow-xl transition-all"
        data-testid="go-to-queue"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline text-blue-200">{t("active_queue")}</div>
            <div className="font-heading font-bold text-2xl mt-1">
              {summary.my_queue_count} {t("queue_waiting")} →
            </div>
          </div>
          <Inbox className="w-10 h-10 opacity-50" />
        </div>
      </Link>
    </>
  );
}

function ContractorDash({ summary, t }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={MapIcon}
          label={t("my_segments")}
          value={summary.my_segments}
          gradient="from-[#3B82F6] to-[#1D4ED8]"
          testid="ctr-segments"
        />
        <StatCard
          icon={TrendingUp}
          label={t("km_completed")}
          value={`${summary.km_completed}/${summary.km_total}`}
          gradient="from-[#10B981] to-[#047857]"
        />
        <StatCard
          icon={ClipboardCheck}
          label={t("inspections")}
          value={summary.total_inspections}
          gradient="from-[#A855F7] to-[#7E22CE]"
        />
        <StatCard
          icon={AlertTriangle}
          label={t("corrections")}
          value={summary.sub_requests}
          gradient="from-[#EF4444] to-[#B91C1C]"
        />
      </div>
      <section className="card-soft p-6 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">{t("my_rating")}</h3>
          <span className="mono text-3xl font-black text-[#2563EB]">
            {summary.rating}%
          </span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
            style={{ width: `${summary.rating}%` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-emerald-50 text-emerald-700 rounded-xl p-3 text-center">
            <div className="mono font-bold text-2xl">{summary.ok}</div>
            <div className="label-overline text-emerald-700">{t("ok")}</div>
          </div>
          <div className="bg-red-50 text-red-700 rounded-xl p-3 text-center">
            <div className="mono font-bold text-2xl">{summary.not_ok}</div>
            <div className="label-overline text-red-700">{t("not_ok")}</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="label-overline mb-1">{t("overall_progress")}</div>
          <div className="flex items-baseline gap-3">
            <span className="mono text-3xl font-bold">{summary.avg_completion}%</span>
            <span className="text-sm text-slate-500">{t("avg_segment_completion")}</span>
          </div>
        </div>
      </section>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [mapData, setMapData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/role-summary"),
      api.get("/dashboard/stats"),
      api.get("/dashboard/map"),
    ]).then(([s, st, m]) => {
      setSummary(s.data);
      setStats(st.data);
      setMapData(m.data);
    });
  }, []);

  if (!summary || !stats) return <div className="mono">{t("loading")}</div>;

  const role = user.role;
  const isAayda = role.startsWith("aayda_");
  const isTeamLead = role === "consulting_team_lead";
  const isSuperAdmin = role === "superadmin";
  // Map visible only to: Team Lead, AAYDA staff, Superadmin
  const showMap = isTeamLead || isAayda || isSuperAdmin;
  // AAYDA dashboard widgets show for: AAYDA, Team Lead, Superadmin
  const showAaydaPanel = isAayda || isTeamLead || isSuperAdmin;
  // Reports CTA visible to: AAYDA, Superadmin (matches sidebar rule)
  const showReportsCta = isAayda || isSuperAdmin;

  let body = null;
  switch (role) {
    case "superadmin":
      body = <SuperAdminDash summary={summary} t={t} />;
      break;
    case "aayda_chairman":
      body = <AAYDAChairmanDash summary={summary} t={t} />;
      break;
    case "aayda_finance":
      body = <AAYDAFinanceDash summary={summary} t={t} />;
      break;
    case "aayda_construction":
      body = <AAYDAConstructionDash summary={summary} t={t} />;
      break;
    case "aayda_tech_supervision":
      body = <AAYDATechSupervisionDash summary={summary} t={t} />;
      break;
    case "contractor":
      body = <ContractorDash summary={summary} t={t} />;
      break;
    default:
      body = <ConsultingDash summary={summary} t={t} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {t("active_label")} · {t(`role_label.${user.role}`)}
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter flex items-center gap-3">
            {t("welcome")}, {user.full_name?.split(" ")[0]}
            <span className="inline-block animate-[wave_2s_ease-in-out_infinite] origin-bottom-right">
              👋
            </span>
          </h1>
          <p className="text-slate-500 mt-2 text-sm">{user.organization_name}</p>
        </div>
        {showReportsCta && (
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-xl bg-white border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] hover:shadow-md transition-all font-semibold"
            data-testid="dashboard-goto-reports"
          >
            <BarChart3 className="w-4 h-4" /> Hesabatlar →
          </Link>
        )}
      </div>

      {body}

      {/* AAYDA / Team Lead / Superadmin overview panel */}
      {showAaydaPanel && <AAYDADashboard />}

      {/* Map — hidden for contractor + non-Team Lead consulting roles */}
      {showMap && (
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapIcon className="w-5 h-5 text-[#004D99]" />
              <h2 className="font-heading font-bold text-xl">
                {t("projects_map")}
              </h2>
            </div>
            <span className="text-xs text-slate-500 mono">
              {mapData?.projects?.length || 0} {t("project_singular")} ·{" "}
              {mapData?.heatmap?.length || 0} {t("correction_zones")}
            </span>
          </div>
          {mapData && (
            <ProjectMap
              projects={mapData.projects}
              segments={mapData.segments}
              heatmap={mapData.heatmap}
              height={420}
            />
          )}
        </section>
      )}

      {/* Recent inspections */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-heading font-bold text-xl mb-4">{t("recent_inspections_title")}</h2>
        <div className="space-y-2">
          {stats.recent_inspections.slice(0, 6).map((i) => (
            <Link
              key={i.id}
              to={`/inspections/${i.id}`}
              className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:bg-slate-50 hover:border-[#004D99]"
              data-testid={`recent-${i.id}`}
            >
              <div>
                <div className="mono text-xs text-slate-500">
                  #{i.id.slice(0, 8)}
                </div>
                <div className="text-sm">
                  Step{" "}
                  {Math.min(i.current_step_index + 1, i.workflow.length)}/
                  {i.workflow.length}
                </div>
              </div>
              <StatusBadge status={i.status} />
            </Link>
          ))}
          {stats.recent_inspections.length === 0 && (
            <div className="text-sm text-slate-500">{t("no_data")}</div>
          )}
        </div>
      </section>
    </div>
  );
}
